import { useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useChatSessions } from '../../../features/chat/chatSessions/useChatSessions';
import { useAutohideScrollbar } from '../../../hooks/useAutohideScrollbar';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import { LiksIconButton } from '../LiksIconButton/LiksIconButton';
import { LiksSearch } from '../LiksSearch/LiksSearch';
import { ChatSessionItem } from './ChatSessionItem';
import { ChatSessionListLoadingItem } from './ChatSessionListLoadingItem';
import { LiksChatSessionsDialogs } from './LiksChatSessionsDialogs';
import { PinnedChatSessionsGroup } from './PinnedChatSessionsGroup';
import './LiksChatSessions.css';

type LiksChatSessionsProps = {
  onSessionClick?: () => void;
};

export function LiksChatSessions({ onSessionClick }: LiksChatSessionsProps) {
  const { translate } = useI18n();
  const navigate = useNavigate();
  const { chatId } = useParams();
  const {
    isAvailable,
    isLoading,
    searchQuery,
    setSearchQuery,
    pinnedSessions,
    unpinnedSessions,
    hasNextPinnedPage,
    hasNextUnpinnedPage,
    isFetchingNextPinnedPage,
    isFetchingNextUnpinnedPage,
    fetchNextPinnedPage,
    fetchNextUnpinnedPage,
    createSession,
    deleteSession,
    leaveSession,
    togglePin,
    renameSession,
    setSessionSystemPrompt,
  } = useChatSessions();
  const groupsScrollbar = useAutohideScrollbar();
  const groupsScrollRef = useRef<HTMLDivElement>(null);
  const [isPinnedExpanded, setIsPinnedExpanded] = useState(true);
  const [openMenuSessionId, setOpenMenuSessionId] = useState<string | null>(
    null,
  );
  const [sessionPendingDelete, setSessionPendingDelete] = useState<{
    id: string;
    title: string;
    isOwner: boolean;
  } | null>(null);
  const [isDeleteBusy, setIsDeleteBusy] = useState(false);
  const [sessionPendingRename, setSessionPendingRename] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [sessionPendingShare, setSessionPendingShare] = useState<{
    id: string;
    title: string;
    isOwner: boolean;
  } | null>(null);
  const [sessionPendingSystemPrompt, setSessionPendingSystemPrompt] = useState<{
    id: string;
    systemPrompt: string;
  } | null>(null);

  const hasAnySessions =
    pinnedSessions.length > 0 || unpinnedSessions.length > 0;
  const showUnpinnedLoadingRow =
    hasNextUnpinnedPage || isFetchingNextUnpinnedPage;

  const handleCreateSession = async () => {
    const session = await createSession(translate('chatSessions.defaultTitle'));
    if (groupsScrollRef.current) {
      groupsScrollRef.current.scrollTop = 0;
    }
    onSessionClick?.();
    navigate(`/platform-chat/${session.id}`);
  };

  const handleDeleteRequest = (session: {
    id: string;
    title: string;
    isOwner: boolean;
  }) => {
    setOpenMenuSessionId(null);
    setSessionPendingDelete(session);
  };

  const handleLeaveFromShare = (sessionId: string, title: string) => {
    setSessionPendingShare(null);
    setSessionPendingDelete({
      id: sessionId,
      title,
      isOwner: false,
    });
  };

  const handlePinToggle = async (sessionId: string) => {
    setOpenMenuSessionId(null);
    try {
      await togglePin(sessionId);
    } catch (error) {
      await toastMutationError(
        error,
        translate('chatSessions.pinErrorTitle'),
        translate('chatSessions.pinErrorDescription'),
      );
    }
  };

  const handleRenameRequest = (session: { id: string; title: string }) => {
    setOpenMenuSessionId(null);
    setSessionPendingRename(session);
  };

  const handleShareRequest = (session: {
    id: string;
    title: string;
    isOwner: boolean;
  }) => {
    setOpenMenuSessionId(null);
    setSessionPendingShare(session);
  };

  const handleSystemPromptRequest = (session: {
    id: string;
    systemPrompt: string;
  }) => {
    setOpenMenuSessionId(null);
    setSessionPendingSystemPrompt(session);
  };

  const handleConfirmSystemPrompt = async (prompt: string) => {
    if (sessionPendingSystemPrompt === null) {
      return;
    }

    try {
      await setSessionSystemPrompt(sessionPendingSystemPrompt.id, prompt);
      toast.success({
        title: translate('chatSessions.systemPromptSaveSuccess'),
      });
      setSessionPendingSystemPrompt(null);
    } catch (error) {
      await toastMutationError(
        error,
        translate('chatSessions.systemPromptTitle'),
        translate('chatSessions.pinErrorDescription'),
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (sessionPendingDelete === null || isDeleteBusy) {
      return;
    }

    const { id, title, isOwner } = sessionPendingDelete;
    setIsDeleteBusy(true);

    try {
      if (isOwner) {
        await deleteSession(id);
        toast.success({
          title: translate('chatSessions.deleteSuccessTitle'),
          description: translate('chatSessions.deleteSuccessDescription', {
            title,
          }),
        });
      } else {
        await leaveSession(id);
        toast.success({
          title: translate('chatSessions.leaveSuccessTitle'),
          description: translate('chatSessions.leaveSuccessDescription', {
            title,
          }),
        });
      }

      if (chatId === id) {
        navigate('/knowledge');
      }

      setSessionPendingDelete(null);
    } catch (error) {
      await toastMutationError(
        error,
        isOwner
          ? translate('chatSessions.deleteChat')
          : translate('chatSessions.leaveChat'),
        translate('chatSessions.pinErrorDescription'),
      );
    } finally {
      setIsDeleteBusy(false);
    }
  };

  const handleConfirmRename = async (title: string) => {
    if (sessionPendingRename === null) {
      return;
    }

    await renameSession(sessionPendingRename.id, title);
    setSessionPendingRename(null);
  };

  const itemProps = {
    onSessionClick,
    openMenuSessionId,
    onMenuOpenChange: setOpenMenuSessionId,
    onRenameRequest: handleRenameRequest,
    onShareRequest: handleShareRequest,
    onSystemPromptRequest: handleSystemPromptRequest,
    onDeleteRequest: handleDeleteRequest,
    onPinToggle: handlePinToggle,
    translate,
  };

  return (
    <section
      className="liks-chat-sessions"
      aria-label={translate('chatSessions.legend')}
    >
      <div className="liks-chat-sessions__toolbar">
        <LiksSearch
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={translate('chatSessions.searchPlaceholder')}
          aria-label={translate('chatSessions.searchLabel')}
          clearLabel={translate('common.search.clear')}
          variant="on-dark"
        />
        <LiksIconButton
          className="liks-chat-sessions__create"
          variant="outlined-on-dark"
          aria-label={translate('chatSessions.create')}
          disabled={!isAvailable || isLoading}
          onClick={() => {
            void handleCreateSession().catch(() => undefined);
          }}
        >
          <Plus strokeWidth={2} aria-hidden />
        </LiksIconButton>
      </div>

      {!hasAnySessions && !isLoading ? (
        <p className="liks-chat-sessions__empty liks-chat-sessions__empty--global">
          {searchQuery.trim() === ''
            ? translate('chatSessions.empty')
            : translate('chatSessions.noResults')}
        </p>
      ) : (
        <div
          ref={groupsScrollRef}
          className={`liks-chat-sessions__groups ${groupsScrollbar.className}`}
          onScroll={groupsScrollbar.onScroll}
        >
          {pinnedSessions.length > 0 && (
            <PinnedChatSessionsGroup
              title={translate('chatSessions.pinned')}
              sessions={pinnedSessions}
              isExpanded={isPinnedExpanded}
              onToggle={() => setIsPinnedExpanded((prev) => !prev)}
              expandLabel={translate('chatSessions.expandPinned')}
              collapseLabel={translate('chatSessions.collapsePinned')}
              hasNextPage={hasNextPinnedPage}
              isFetchingNextPage={isFetchingNextPinnedPage}
              onLoadMore={fetchNextPinnedPage}
              {...itemProps}
            />
          )}

          {(unpinnedSessions.length > 0 || showUnpinnedLoadingRow) && (
            <ul className="liks-chat-sessions__list liks-chat-sessions__list--recent">
              {unpinnedSessions.map((session) => (
                <ChatSessionItem
                  key={session.id}
                  session={session}
                  {...itemProps}
                />
              ))}
              {showUnpinnedLoadingRow && (
                <ChatSessionListLoadingItem
                  enabled={hasNextUnpinnedPage && !isFetchingNextUnpinnedPage}
                  isFetching={isFetchingNextUnpinnedPage}
                  onVisible={fetchNextUnpinnedPage}
                />
              )}
            </ul>
          )}
        </div>
      )}

      <LiksChatSessionsDialogs
        sessionPendingShare={sessionPendingShare}
        sessionPendingRename={sessionPendingRename}
        sessionPendingDelete={sessionPendingDelete}
        sessionPendingSystemPrompt={sessionPendingSystemPrompt}
        isDeleteBusy={isDeleteBusy}
        renameTitle={translate('chatSessions.renameTitle')}
        renameLabel={translate('chatSessions.renameLabel')}
        renameConfirm={translate('chatSessions.renameConfirm')}
        renameCancel={translate('chatSessions.renameCancel')}
        systemPromptTitle={translate('chatSessions.systemPromptTitle')}
        systemPromptLabel={translate('chatSessions.systemPromptLabel')}
        systemPromptConfirm={translate('chatSessions.systemPromptConfirm')}
        systemPromptCancel={translate('chatSessions.systemPromptCancel')}
        onLeaveFromShare={handleLeaveFromShare}
        onCloseShare={() => setSessionPendingShare(null)}
        onConfirmRename={(title) => {
          void handleConfirmRename(title).catch(() => undefined);
        }}
        onCancelRename={() => setSessionPendingRename(null)}
        onConfirmDelete={() => {
          void handleConfirmDelete().catch(() => undefined);
        }}
        onCancelDelete={() => {
          if (!isDeleteBusy) {
            setSessionPendingDelete(null);
          }
        }}
        onConfirmSystemPrompt={(prompt) => {
          void handleConfirmSystemPrompt(prompt).catch(() => undefined);
        }}
        onCancelSystemPrompt={() => setSessionPendingSystemPrompt(null)}
      />
    </section>
  );
}
